import type { Properties } from "posthog-js";

import type { UpgradeReason } from "@sill/shared/components/billing/pricing-copy";

export const ONBOARDING_EVENTS = {
  assessmentStarted: "assessment_started",
  assessmentStepCompleted: "assessment_step_completed",
  assessmentCompleted: "assessment_completed",
  plaidLinkOpened: "plaid_link_opened",
  plaidLinkSucceeded: "plaid_link_succeeded",
  plaidLinkFailed: "plaid_link_failed",
  recommendationViewed: "recommendation_viewed",
  onboardingCtaClicked: "onboarding_cta_clicked",
} as const;

export const BUYER_FUNNEL_EVENTS = {
  inviteOpened: "buyer.invite_opened",
} as const;

export const SOURCE_CHOOSER_EVENTS = {
  selected: "source_chooser_selected",
} as const;

// Conversion funnel for the focused single-action upsell modal:
// opens → upgrades vs compares vs dismisses, sliceable by `feature`
// (the Plus-gated action the user just tried). Distinct from the
// pricing-comparison `UpgradeSheet` events — see UpgradeModal.tsx.
export const UPGRADE_MODAL_EVENTS = {
  Opened: "upgrade_modal_opened",
  UpgradeClicked: "upgrade_modal_upgrade_clicked",
  ComparePlansClicked: "upgrade_modal_compare_clicked",
  Dismissed: "upgrade_modal_dismissed",
} as const;

export const ONBOARDING_STEP_NAMES = [
  "buyer_type",
  "target_area",
  "income",
  "savings",
  "credit_score",
  "monthly_debts",
  "work_history",
  "budget",
  "timeline",
  "housing_situation",
  "email_capture",
] as const;

export type OnboardingEventName =
  (typeof ONBOARDING_EVENTS)[keyof typeof ONBOARDING_EVENTS];
export type BuyerFunnelEventName =
  (typeof BUYER_FUNNEL_EVENTS)[keyof typeof BUYER_FUNNEL_EVENTS];
export type SourceChooserEventName =
  (typeof SOURCE_CHOOSER_EVENTS)[keyof typeof SOURCE_CHOOSER_EVENTS];
export type UpgradeModalEventName =
  (typeof UPGRADE_MODAL_EVENTS)[keyof typeof UPGRADE_MODAL_EVENTS];

export type DataSourceChooserSelection = {
  resultingTier: "self-reported" | "document-backed" | "bank-verified";
  source: "plaid" | "doc" | "manual";
};

type AnalyticsEventName =
  | BuyerFunnelEventName
  | OnboardingEventName
  | SourceChooserEventName
  | UpgradeModalEventName;

type AnalyticsCapture = (
  event: AnalyticsEventName,
  properties?: Properties,
) => void;

export function captureAssessmentStarted(
  capture: AnalyticsCapture,
  properties?: Properties,
) {
  capture(ONBOARDING_EVENTS.assessmentStarted, properties);
}

const capturedBuyerInviteOpened = new Set<string>();
const buyerInviteOpenedSessionKeyPrefix = "sill.buyer_invite_opened.";

export function captureBuyerInviteOpenedOnce(
  capture: AnalyticsCapture,
  agentInviteCode: string | null | undefined,
  properties?: Properties,
) {
  const code = agentInviteCode?.trim().toUpperCase();
  if (!code) return false;

  const key = `${buyerInviteOpenedSessionKeyPrefix}${code}`;
  if (capturedBuyerInviteOpened.has(key)) return false;
  if (typeof window !== "undefined") {
    try {
      if (window.sessionStorage.getItem(key) === "1") {
        capturedBuyerInviteOpened.add(key);
        return false;
      }
      window.sessionStorage.setItem(key, "1");
    } catch {
      // Storage can be unavailable in privacy modes; module-level dedupe still
      // protects the current page lifecycle.
    }
  }

  capturedBuyerInviteOpened.add(key);
  capture(BUYER_FUNNEL_EVENTS.inviteOpened, {
    agent_invite_code: code,
    entry_surface: "consumer_onboarding",
    referral_source: "referral_link",
    ...(properties ?? {}),
  });
  return true;
}

export function captureAssessmentStepCompleted(
  capture: AnalyticsCapture,
  properties: {
    assessment_id?: string;
    step_index: number;
    step_name: (typeof ONBOARDING_STEP_NAMES)[number];
  },
) {
  capture(ONBOARDING_EVENTS.assessmentStepCompleted, properties);
}

export function captureAssessmentCompleted(
  capture: AnalyticsCapture,
  properties?: Properties,
) {
  capture(ONBOARDING_EVENTS.assessmentCompleted, properties);
}

export function captureRecommendationViewed(
  capture: AnalyticsCapture,
  properties?: Properties,
) {
  capture(ONBOARDING_EVENTS.recommendationViewed, properties);
}

export function captureOnboardingCtaClicked(
  capture: AnalyticsCapture,
  properties: {
    cta_name: string;
    location: string;
  },
) {
  capture(ONBOARDING_EVENTS.onboardingCtaClicked, properties);
}

export function captureSourceChooserSelected(
  capture: AnalyticsCapture,
  properties: DataSourceChooserSelection,
) {
  capture(SOURCE_CHOOSER_EVENTS.selected, properties);
}

// Every upgrade-modal event carries `feature` so analytics can slice the
// open / upgrade / compare / dismiss funnel by which Plus-gated action
// triggered the modal. Caller passes the PostHog `capture` (DI-style,
// matching the rest of this module) plus the event variant and feature.
export function captureUpgradeModalEvent(
  capture: AnalyticsCapture,
  event: keyof typeof UPGRADE_MODAL_EVENTS,
  feature: UpgradeReason,
  properties?: Properties,
) {
  capture(UPGRADE_MODAL_EVENTS[event], { feature, ...(properties ?? {}) });
}
